// PURPOSE: Home as a numbers bar over one list: each panel in the layout becomes a count, picking one shows its list.
// CONTRACT: Home option C chosen by Phin 2026-10-18; panel set and order still come from the saved layout (spec 059).
// DEPENDS: Home data and the panel bodies home.tsx already renders.
// CEILING: Presentation only; rows keep their own links and actions.
import { useEffect, useState, type MouseEvent, type ReactNode } from 'react';
import { NumbersBar } from './numbers-bar';
import { Link, useNavigate } from 'react-router-dom';
import { FROM_HOME } from './back-link';
import { Section } from './home-panels';
import type { HomeData, HomePanel, OutreachPanelKey } from './types';

// To-do and This week sit beside the list instead of becoming numbers.
export const SIDE_PANELS = new Set(['todo', 'this_week']);
const OUTREACH: { key: OutreachPanelKey; label: string; tab: string }[] = [
 { key: 'outreach_screening', label: 'In screening', tab: 'screening' },
 { key: 'outreach_claimed', label: 'Claimed, not emailed', tab: 'claimed' },
 { key: 'outreach_due', label: 'Emails due', tab: 'followups' },
 { key: 'outreach_calls', label: 'Calls to make', tab: 'calling' },
 { key: 'outreach_active', label: 'Active, needs follow-up', tab: 'active' },
];
const LABELS: Record<string, string> = { needs_you: 'Need you', errors: 'Errors', builds: 'Active builds', my_builds: 'Your builds', tickets: 'Tickets', my_tickets: 'My tickets', pipeline: 'In pipeline', quiet: 'Gone quiet', my_clients: 'Your clients', sent: 'Sent to others', outreach: 'Outreach' };
export type HomeTile = { key: string; label: string; count: number; alert: boolean };

export function tileCount(data: HomeData, key: string): number {
 switch (key) {
  case 'needs_you': return data.needs_you ? data.needs_you.reduce((n, g) => n + g.count, 0) : (data.overdue?.length || 0) + (data.today?.length || 0);
  case 'errors': return data.errors?.length || 0;
  case 'builds': return data.builds?.length || 0;
  case 'my_builds': return data.my_builds?.length || 0;
  case 'tickets': return data.tickets?.length || 0;
  case 'my_tickets': return data.my_tickets?.length || 0;
  case 'pipeline': return data.stages?.filter(s => s.stage !== 'completed').reduce((n, s) => n + s.count, 0) || 0;
  case 'quiet': return data.quiet_clients?.filter(c => c.days_quiet >= 7).length || 0;
  case 'my_clients': return data.clients?.length || 0;
  case 'sent': return data.todo_sent?.open.length || 0;
  case 'outreach': return OUTREACH.reduce((n, o) => n + (data[o.key]?.count || 0), 0);
  default: return 0;
 }
}

// The five outreach counters fold into one Outreach number at the first counter's place in the layout.
export function homeTiles(data: HomeData, panels: HomePanel[]): HomeTile[] {
 const tiles: HomeTile[] = [];
 for (const panel of panels) {
  if (SIDE_PANELS.has(panel.key)) continue;
  const key = panel.key.startsWith('outreach_') ? 'outreach' : panel.key;
  if (tiles.some(t => t.key === key)) continue;
  const count = tileCount(data, key);
  tiles.push({ key, label: LABELS[key] || key, count, alert: key === 'errors' && count > 0 });
 }
 return tiles;
}

// Home opens on the first number with something in it, in layout order; nothing is remembered between visits.
export function firstTile(tiles: HomeTile[]) { return (tiles.find(t => t.count > 0) || tiles[0])?.key; }

export function OutreachList({ data }: { data: HomeData }) {
 return <Section title="Outreach" to="/outreach" link="Open outreach"><ul className="hm-list home-outreach-list">{OUTREACH.map(o => {
  const panel = data[o.key]; const count = panel?.count ?? 0;
  const to = panel?.href || panel?.link || `/outreach?tab=${o.tab}${o.key === 'outreach_screening' ? '' : '&owned=1'}`;
  return <li key={o.key} className={count ? undefined : 'is-zero'}><Link className="hm-title" to={to}>{o.label}</Link><strong>{count}</strong></li>;
 })}</ul></Section>;
}

// A click anywhere on a list row follows that row's first link, unless it landed on a control of its own.
function followRow(event: MouseEvent) {
 const target = event.target as HTMLElement;
 if (target.closest('a,button,input,label,summary,select,textarea,form,[role="menu"],.hm-err-item') || window.getSelection()?.toString()) return;
 target.closest('li, .home-row, tr')?.querySelector<HTMLAnchorElement>('a[href]')?.click();
}

// The saved layout still places To-do / This week: before the list puts them on the left, Full stacks them on their own row.
export function boardArrangement(panels: HomePanel[]) {
 const sideAt = panels.findIndex(p => SIDE_PANELS.has(p.key)); const listAt = panels.findIndex(p => !SIDE_PANELS.has(p.key));
 const sideFirst = sideAt >= 0 && (listAt < 0 || sideAt < listAt);
 const stacked = panels.some(p => SIDE_PANELS.has(p.key) && p.size === 'full') || (listAt >= 0 && panels[listAt].size === 'full');
 return { sideFirst, stacked };
}
export function HomeBoard({ tiles, list, side, sideFirst = false, stacked = false }: { tiles: HomeTile[]; list(key: string): ReactNode; side: ReactNode; sideFirst?: boolean; stacked?: boolean }) {
 const [picked, setPicked] = useState<string>(); const navigate = useNavigate();
 // Links opened from Home carry where they came from, so the record page's back link returns here.
 const fromHome = (event: MouseEvent) => {
  const link = (event.target as HTMLElement).closest('a');
  const href = link?.getAttribute('href');
  if (!href?.startsWith('/') || link?.target === '_blank' || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
  event.preventDefault(); navigate(href, { state: { from: FROM_HOME } });
 };
 const key = tiles.some(t => t.key === picked) ? picked : firstTile(tiles);
 const keys = tiles.map(t => t.key).join();
 // The summary line links to #home-<panel>; following one picks that number.
 useEffect(() => {
  const pick = () => { const name = window.location.hash.replace(/^#home-/, ''); const tile = name.startsWith('outreach_') ? 'outreach' : name; if (keys.split(',').includes(tile)) setPicked(tile); };
  pick(); window.addEventListener('hashchange', pick); return () => window.removeEventListener('hashchange', pick);
 }, [keys]);
 return <>
  <NumbersBar tiles={tiles} picked={key} onPick={setPicked} label="Show on Home" />
  <div className={`home-board${side ? '' : ' no-side'}${side && sideFirst ? ' side-first' : ''}${side && stacked ? ' stacked' : ''}`} onClickCapture={fromHome}>
   {key && <div className="home-board-main" id={`home-${key}`} onClick={followRow}>{list(key)}</div>}
   {side && <div className="home-board-side">{side}</div>}
  </div>
 </>;
}
